import React, { useState, useEffect } from "react";
import {
  Toolbar,
  Typography,
  Button,
  Grid,
  Box,
  CircularProgress,
  Chip,
  Paper,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar, // Import Snackbar
  Alert,      // Import Alert for better UI
} from "@mui/material";
import {
  ManageSearch as ManageSearchIcon,
  FindInPage as FindInPageIcon,
  AddTaskOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
 
import { getAccountsByPlatform } from "../services/socialMediaAccountsService";
import { getPostHistoryall } from "../services/postHistoryService";
import Header from "../Components/header/Header";
 
import { API_BASE_URL, apiGet, apiPost } from "../services/api";
import HexaLoader from "../Components/loader/Loader";
import Sidebar from "../Components/sideBar/sideBar";
import PostCard from "../Components/postCard/LinkedinPostcard";
 
const drawerWidth = 260;
 
const LinkedInManagement = () => {
  const navigate = useNavigate();
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkedinKeywords, setLinkedinKeywords] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [tweets, setTweets] = useState([]);
  const [selectedKeywordForPrompts, setSelectedKeywordForPrompts] = useState(null);
  const [keywordPromptsDialogOpen, setKeywordPromptsDialogOpen] = useState(false);
  const [dataSource, setDataSource] = useState("cache");
  const [postedPosts, setPostedPosts] = useState([]);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState("");
  const [keywordPromptsMap, setKeywordPromptsMap] = useState({});
  const [keywordPrompts, setKeywordPrompts] = useState([]);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [linkedinAccounts, setLinkedinAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedTweet, setSelectedTweet] = useState(null);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [editedReply, setEditedReply] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [modeluse,setModeluse]=useState("gemini-2.5-flash");
  const [keywords, setKeywords] = useState([]);
  const [twitterAccounts, setTwitterAccounts] = useState([]);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPromptContent, setEditedPromptContent] = useState("");
 
  // --- State for MUI Snackbar ---
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // can be 'success', 'error', 'warning', 'info'
  });
 
  // --- Helper function to show the snackbar ---
  const showToast = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
 
  // --- Handler to close the snackbar ---
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };
 
  const fetchLinkedInAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await getAccountsByPlatform("linkedin");
      const accounts = response.data || [];
      setLinkedinAccounts(accounts);
      setTwitterAccounts(accounts);
 
      const defaultAccount = accounts.find((account) => account.isDefault === true);
      if (defaultAccount) {
        setSelectedAccount(defaultAccount.id);
      } else {
        setSelectedAccount(null);
      }
      showToast("LinkedIn accounts fetched successfully!", "success");
    } catch (err) {
      showToast("Failed to fetch LinkedIn accounts.", "error");
      setLinkedinAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };
 
  const fetchAllKeywords = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/keywords`, {
        headers: { Authorization: `Bearer ${token || "dummy-token"}` },
      });
      return response.data.data || [];
    } catch (err) {
      showToast("Failed to fetch keywords.", "error");
      return [];
    }
  };
 
  const fetchPostHistory = async () => {
    try {
      const result = await getPostHistoryall();
      if (result.success && result.data) {
        const linkedinPostIds = result.data.map((post) => post.tweetid);
        setPostedPosts(linkedinPostIds);
        showToast("Post history fetched successfully.", "success");
      }
    } catch (err) {
      showToast("Failed to fetch post history.", "error");
    }
  };
 
  useEffect(() => {
    fetchLinkedInAccounts();
    fetchPostHistory();
    fetchPrompts();
    fetchKeywordPromptAssociations();
  }, []);
 
  const fetchKeywordPromptAssociations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/linkedin-keywords`);
      if (response.data.success && response.data.data) {
        const keywordData = response.data.data;
        const promptMap = {};
        const keywordPromptsMap = {};
 
        keywordData.forEach((keyword) => {
          if (keyword.promptId) {
            promptMap[keyword.text] = keyword.promptId.toString();
          }
          if (keyword.prompts && keyword.prompts.length > 0) {
            keywordPromptsMap[keyword.text] = keyword.prompts;
          }
        });
 
        setKeywordPromptsMap(keywordPromptsMap);
        showToast("Keyword-prompt associations loaded.", "info");
      }
    } catch (error) {
      showToast("Error fetching keyword-prompt associations.", "error");
    }
  };
 
  const fetchPrompts = async (keywordId = null) => {
    try {
      let url = `${API_BASE_URL}/prompts`;
      if (keywordId) {
        url += `?keywordId=${keywordId}`;
      }
      const response = await axios.get(url);
      if (response.data.success && response.data.data) {
        const dbPrompts = response.data.data.map((prompt) => ({
          id: prompt.id.toString(),
          name: prompt.name,
          content: prompt.content,
          model: prompt.model,
        }));
        if (keywordId) {
          return dbPrompts;
        } else {
          setAvailablePrompts(dbPrompts);
          const defaultPrompt = dbPrompts.find((p) => p.name === "Default");
          if (defaultPrompt) setSelectedPromptId(defaultPrompt.id);
        }
      }
    } catch (error) {
      showToast("Error fetching prompts.", "error");
    }
    return [];
  };
 
  useEffect(() => {
    if (linkedinAccounts.length > 0 && selectedAccount) {
      fetchAllPosts(selectedAccount);
    }
  }, [selectedAccount, linkedinAccounts]);
 
  const fetchAllLinkedInKeywords = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/linkedin-keywords`, {
        headers: { Authorization: `Bearer ${token || "dummy-token"}` },
      });
      return response.data.data || [];
    } catch (err) {
      showToast("Failed to fetch LinkedIn keywords.", "error");
      return [];
    }
  };
 ;
// const fetchAllPosts = async (accountId = selectedAccount, forceRefresh = false) => {
//     setLoading(true);
//     setLinkedinPosts([]);
//     setDataSource("cache");
//     try {
//       const allKeywords = await fetchAllLinkedInKeywords();
//       const filteredKeywords = accountId
//         ? allKeywords.filter((k) => String(k.accountId) === String(accountId))
//         : allKeywords;
//       setLinkedinKeywords(filteredKeywords);
//       if (filteredKeywords.length === 0) {
//         showToast("No keywords configured for this account.", "warning");
//         setLoading(false);
//         return;
//       }
//       let allFetchedPosts = [];
//       for (const keyword of filteredKeywords) {
//         const { minLikes = 0, minComments = 0, minConnections = 0, text } = keyword;
//         let searchUrl = `${API_BASE_URL}/search/linkedin?keyword=${text}&minLikes=${minLikes}&minComments=${minComments}&minConnections=${minConnections}`;
//         if (accountId) searchUrl += `&accountId=${accountId}`;
 
//       // --- START: MODIFICATION ---
//       const user = JSON.parse(localStorage.getItem("user"));
//       const userId = user?.id;
//       console.log(userId,"000000000");
//       if (userId) {
//         searchUrl += `&userId=${userId}`;
//       }
//         if (forceRefresh) searchUrl += `&forceRefresh=true`;
//         try {
//           const res = await axios.get(searchUrl);
//           if (res.data.from) setDataSource(res.data.from);
//           let keywordPosts = (res.data.posts || []).map((post) => ({
//             ...post,
//             keyword: text,
//             platform: "linkedin",
//           }));
//           allFetchedPosts = [...allFetchedPosts, ...keywordPosts];
//         } catch (err) {
//           showToast(`Error fetching posts for keyword "${text}".`, "error");
//         }
//       }
//       const uniquePosts = Array.from(new Map(allFetchedPosts.map((post) => [post.id, post])).values());
//       setLinkedinPosts(uniquePosts);
//       setTweets(uniquePosts);
//       showToast(`Found ${uniquePosts.length} unique posts.`, "success");
//     } catch (err) {
//       showToast("Failed to fetch LinkedIn posts.", "error");
//     } finally {
//       setLoading(false);
//     }
//   };

const fetchAllPosts = async (accountId = selectedAccount, forceRefresh = false) => {
setLoading(true);
setLinkedinPosts([]);
setDataSource("cache");
 
// A check to ensure an account is selected before proceeding.
if (!accountId) {
showToast("Please select a LinkedIn account to fetch posts.", "warning");
setLoading(false);
return;
}
 
try {
const allKeywords = await fetchAllLinkedInKeywords();
const filteredKeywords = accountId
? allKeywords.filter((k) => String(k.accountId) === String(accountId))
: allKeywords;
setLinkedinKeywords(filteredKeywords);
 
if (filteredKeywords.length === 0) {
showToast("No keywords configured for this account.", "warning");
setLoading(false);
return;
}
 
let allFetchedPosts = [];
let accountErrorShown = false; // Flag to prevent repeated error messages.
 
for (const keyword of filteredKeywords) {
const { minLikes = 0, minComments = 0, minConnections = 0, text } = keyword;
 
let searchUrl = `/search/linkedin?keyword=${text}&minLikes=${minLikes}&minComments=${minComments}&minConnections=${minConnections}&language=en`;
if (accountId) searchUrl += `&accountId=${accountId}`;
 
const user = JSON.parse(localStorage.getItem("user"));
const userId = user?.id;
if (userId) searchUrl += `&userId=${userId}`;
if (forceRefresh) searchUrl += `&forceRefresh=true`;
 
try {
const res = await apiGet(searchUrl);
if (res.from) setDataSource(res.from);
 
let keywordPosts = (res.posts || []).map((post) => ({
...post,
keyword: text,
platform: "linkedin",
}));
if (keywordPosts.length === 0){
showToast(`No posts found for keyword "${text}".`, "info");
}
allFetchedPosts = [...allFetchedPosts, ...keywordPosts];
 
}
catch (err) {
// --- MODIFICATION START ---
// Check for the specific error message from the API response.
console.log("Error fetching posts:", err);
console.log(err,"1111111111111");
 if (err.response?.data?.message === "Token expired, Try Closing AI based Fillteration") {
    showToast(err.response.data.message, "error");
    // You might want to 'break' here if this error means subsequent calls will also fail
    break; 
  }
if (err =="Error: LinkedIn account not found for user") {
showToast("Your default fetch account is not selected or not found. Please choose a valid account.", "error");
accountErrorShown = true; // Set the flag to show the message only once.
break; // Exit the loop as other requests will also fail.
} else if (!accountErrorShown) {
// Show other errors only if the main account error hasn't occurred.
showToast(`Error fetching posts for keyword "${text}".`, "error");
}
// --- MODIFICATION END ---
}
}
 
if (!accountErrorShown) {
const uniquePosts = Array.from(
new Map(allFetchedPosts.map((post) => [post.id, post])).values()
);
if(uniquePosts.length === 0){
showToast("Token expired, Try Closing AI based Fillteration", "error");
}
else{showToast(`Found ${uniquePosts.length} unique posts.`, "success");}
setLinkedinPosts(uniquePosts);
setTweets(uniquePosts);

}
 
} catch (err) {
showToast("Failed to fetch LinkedIn posts.", "error");
} finally {
setLoading(false);
}
};



 
  const handleKeywordToggle = (keywordText) => {
    setSelectedKeywords((prev) => {
      const newSelected = prev.includes(keywordText)
        ? prev.filter((k) => k !== keywordText)
        : [...prev, keywordText];
      return newSelected;
    });
  };
 
  const handlePost = async (tweet) => {
    setSelectedTweet(tweet);
    setEditedReply(tweet.reply || "");
    setIsEditingPrompt(false);
    setEditedPromptContent("");
    setPostDialogOpen(true);
   let modelForGeneration = "gemini-2.5-flash"; // Start with the default model

    try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/prompts/keyword/${tweet.keyword}`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        const promptData = response.data.data;
        
        if (promptData && promptData.model) {
            console.log(`Custom model found for keyword "${tweet.keyword}": ${promptData.model}`);
            // Assign the new model to our local variable
            modelForGeneration = promptData.model;
        }
    } catch (error) {
        if (error.response && error.response.status !== 404) {
            console.error("Error fetching prompt settings:", error);
            showToast("Could not load AI settings for this keyword.", "error");
        }
        // If an error occurs, modelForGeneration remains the default, which is fine.
    }

    // Update the state for future renders (e.g., for the "Regenerate" button)
    setModeluse(modelForGeneration);
    
    // Auto-generate reply ONLY if one doesn't already exist
    if (!tweet.reply) {
        // Pass the determined model directly as a parameter
        generateReply(tweet, true, modelForGeneration);
    } else {
        // If a reply exists, just show it
        setEditedReply(tweet.reply);
    }
    // Auto-generate reply when opening the dialog
    if (!tweet.reply) {
      generateReply(tweet, true); // Pass a flag to indicate it's an auto-call
    }
  };
 
  const handlePostSubmit = async () => {
    if (!selectedTweet || !editedReply) return;
    setIsPosting(true);
    try {
      const keywordObj = linkedinKeywords.find(k => k.text === selectedTweet.keyword);
const keywordId = keywordObj?.id || null;
console.log("Keyword ID:", keywordId);
      let selectedPrompt = availablePrompts.find((p) => p.id === selectedPromptId);
      const model = selectedPrompt?.model || "meta-llama/llama-3-8b-instruct";
      const promptContent = selectedPrompt?.content || "";
      const promptName = selectedPrompt?.name || "Default";
 
      const response = await apiPost(`/reply-to-linkedin-post`, {
        tweetId: selectedTweet.id,
        replyText: editedReply,
        selectedAccountId: selectedAccount,
        tweetText: selectedTweet.text,
        model,
        promptContent,
        promptName,
        keywordId: keywordId,
        keywordText: selectedTweet.keyword,
      });
 
      if (response.success) {
        showToast("Reply posted successfully!", "success");
        setPostedPosts((prev) => [...prev, selectedTweet.id]);
        setPostDialogOpen(false);
      } else {
        showToast(`Failed to post reply: ${response.data.message || "Unknown error"}`, "error");
      }
    } catch (error) {
      console.log("Error",error)
      // showToast(`Error posting reply: ${error.message || "Unknown error"}`, "error");
    } finally {
      setIsPosting(false);
    }
  };
 
  useEffect(() => {
    const filtered = selectedKeywords.length === 0
      ? linkedinPosts
      : linkedinPosts.filter((post) => selectedKeywords.includes(post.keyword));
    setFilteredPosts(filtered);
  }, [linkedinPosts, selectedKeywords]);
 
  const generateReply = async (tweetToProcess, isAuto = false, modelToUse) => {
    const currentTweet = tweetToProcess || selectedTweet;
    if (!currentTweet) return;

    if (!isAuto) setIsGeneratingReply(true);
    setEditedReply("Generating reply...");

    try {
        let selectedPrompt = availablePrompts.find((p) => p.id === selectedPromptId);
        if (!selectedPrompt) {
            selectedPrompt = {
                id: 'default',
                name: 'Default',
                content: 'Reply smartly to this LinkedIn post:\n"{postContent}"\nMake it professional, engaging, and relevant.',
                model: 'gemini-2.5-flash',
            };
        }

        const formattedPrompt = selectedPrompt.content.replace(/{postContent}|{tweetContent}/g, currentTweet.text);

        // Use the passed-in model from handlePost, or fall back to the state for "Regenerate"
        const finalModel = modelToUse || modeluse;

        const response = await apiPost(`/generate-reply`, {
            model: finalModel, // Use the correctly determined model
            messages: [{ role: "user", content: formattedPrompt }],
        });

        // This console log will now show the correct model being used
        console.log("Using model for generation:", finalModel);
        
        if (response?.reply) {
            setEditedReply(response.reply);
            if (!isAuto) showToast("New reply generated!", "success");
        } else {
            setEditedReply("");
            showToast("Failed to generate reply.", "error");
        }
    } catch (error) {
        setEditedReply("");
        showToast("Error generating reply: " + (error.response?.data?.message || error.message), "error");
    } finally {
        if (!isAuto) setIsGeneratingReply(false);
    }
};
 
  return (
    <Box sx={{ display: "flex" }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
 
      <Sidebar drawerWidth={drawerWidth} onNavigate={navigate} />
 
      <Box component="main" sx={{ flexGrow: 1, bgcolor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#4D99A3" }}>
                LinkedIn Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage LinkedIn keyword scraping and post engagement
              </Typography>
            </Box>
          </Box>
         
           <Grid container spacing={3} sx={{ mb: 4 ,width: "100%"}}>
            <Grid item xs={12} md={6} sx={{ flex: 1 }} >
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: "#e5efee",height: "100%"}}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ bgcolor: "#4D99A3", color: "white", width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FindInPageIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Search for LinkedIn Posts
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                      Fetch the latest posts matching your keywords.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => fetchAllPosts(selectedAccount, true)}
                      disabled={loading || !selectedAccount}
                      sx={{ borderRadius: 2, backgroundColor: "#4D99A3", '&:hover': { backgroundColor: '#438a94' }}}
                    >
                      {loading ? "Fetching..." : "Fetch New"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
           <Grid item xs={12} md={6} sx={{ flex: 1 }}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: "#e5efee" ,height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ bgcolor: "#4D99A3", color: "white", width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ManageSearchIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Manage LinkedIn Keywords
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                      Add or edit keywords to customize your search results.
                    </Typography>
                    <Button
  variant="contained"
  onClick={() => navigate("/social-media-settings?tab=1")} 
  sx={{ borderRadius: 2, backgroundColor: "#4D99A3", '&:hover': { backgroundColor: '#438a94' }}}
>
  Manage Keywords
</Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
          </Grid>
 
          <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 3,width:'92%' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Your LinkedIn Keywords & Posts
              </Typography>
              <TextField
                select
                size="small"
                label="LinkedIn Account"
                value={selectedAccount || ""}
                onChange={(e) => setSelectedAccount(e.target.value)}
                disabled={loadingAccounts}
                sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                {linkedinAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.accountName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {linkedinKeywords.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {linkedinKeywords.map((keyword) => (
                  <Chip
                    key={keyword.id}
                    label={keyword.text}
                    onClick={() => handleKeywordToggle(keyword.text)}
                    color={selectedKeywords.includes(keyword.text) ? "primary" : "default"}
                    sx={{
                      borderRadius: "16px",
                      fontWeight: 500,
                      backgroundColor: selectedKeywords.includes(keyword.text) ? "#4D99A3" : "#ffffff",
                      color: selectedKeywords.includes(keyword.text) ? "#ffffff" : "#4D99A3",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>
 
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filteredPosts.length > 0 ? (
            <Grid container spacing={3}>
              {filteredPosts.map((post, i) => (
                <Grid item xs={12} sm={6} lg={4} key={post.id || i}>
                  <PostCard
                    tweet={post}
                    postedTweets={postedPosts}
                    dataSource={dataSource}
                    handlePost={() => handlePost(post)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography sx={{ textAlign: 'center', mt: 5, color: 'text.secondary' }}>
              No posts found. Try fetching new posts or adjusting your keywords.
            </Typography>
          )}
        </Box>
      </Box>
 
      {/* Post Reply Dialog */}
      <Dialog open={postDialogOpen} onClose={() => setPostDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "var(--brand-font)", fontWeight: 600 }}>
          Post Reply
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Original Post
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: "8px", fontStyle: "italic" }}>
            {selectedTweet?.text}
          </Typography>
         
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Generated Reply
            </Typography>
            <Button onClick={() => generateReply()} disabled={isGeneratingReply} size="small">
              {isGeneratingReply ? <CircularProgress size={20} /> : 'Regenerate'}
            </Button>
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={editedReply}
            onChange={(e) => setEditedReply(e.target.value)}
            placeholder="Edit your reply..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => setPostDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handlePostSubmit}
            variant="contained"
            disabled={isPosting || !editedReply.trim() || editedReply === "Generating reply..."}
            startIcon={isPosting ? <CircularProgress size={20} color="inherit" /> : <AddTaskOutlined />}
            sx={{ backgroundColor: "#4D99A3", '&:hover': { backgroundColor: '#438a94' } }}
          >
            {isPosting ? "Posting..." : "Post Reply"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
 
export default LinkedInManagement;